import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';
import type {
  DialOpCallType,
  DialOpComparisonPreset,
  DialOpLeadPool,
  DialOpPeriodGrain,
  DialOpReportCardResponse,
  DialOpRosterResponse,
  DialOpSnapshot,
} from '../types/dialingOpportunity';

export interface DialOpQuery {
  grain?: DialOpPeriodGrain;
  preset?: DialOpComparisonPreset;
  currentStart?: string;
  currentEnd?: string;
  compareStart?: string;
  compareEnd?: string;
  state?: string[] | null;
  agent?: string[] | null;
  refresh?: boolean;
}

export interface DialOpReportCardQuery {
  currentStart: string;
  currentEnd: string;
  callType?: DialOpCallType;
  leadPool?: DialOpLeadPool;
  state?: string[] | null;
  agent?: string[] | null;
  limit?: number;
  offset?: number;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

function requireTenantPrefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return tenantApiPrefix(tid);
}

async function readJson<T>(response: Response, fallbackMessage: string): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: string }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallbackMessage);
  }
  return response.json();
}

function queryString({
  grain = 'week',
  preset = 'latest_vs_prior',
  currentStart,
  currentEnd,
  compareStart,
  compareEnd,
  state,
  agent,
  refresh,
}: DialOpQuery): string {
  const params = new URLSearchParams({ grain, preset });
  if (currentStart && currentEnd) {
    params.set('current_start', currentStart);
    params.set('current_end', currentEnd);
    if (compareStart && compareEnd) {
      params.set('compare_start', compareStart);
      params.set('compare_end', compareEnd);
    }
  }
  for (const st of state ?? []) params.append('state', st);
  for (const a of agent ?? []) params.append('agent', a);
  if (refresh) params.set('refresh', 'true');
  return params.toString();
}

export async function fetchDialOpSnapshot(query: DialOpQuery = {}): Promise<DialOpSnapshot> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/dialing-opportunity/snapshot?${queryString(query)}`),
    { headers: getAuthHeaders() },
  );
  return readJson<DialOpSnapshot>(response, 'Failed to load dialing opportunity snapshot');
}

function reportCardQueryString({
  currentStart,
  currentEnd,
  callType = 'all',
  leadPool = 'all',
  state,
  agent,
  limit = 200,
  offset = 0,
}: DialOpReportCardQuery): string {
  const params = new URLSearchParams({
    current_start: currentStart,
    current_end: currentEnd,
    call_type: callType,
    lead_pool: leadPool,
    limit: String(limit),
    offset: String(offset),
  });
  for (const st of state ?? []) params.append('state', st);
  for (const a of agent ?? []) params.append('agent', a);
  return params.toString();
}

export async function fetchDialOpReportCard(
  query: DialOpReportCardQuery,
): Promise<DialOpReportCardResponse> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/dialing-opportunity/report-card?${reportCardQueryString(query)}`),
    { headers: getAuthHeaders() },
  );
  return readJson<DialOpReportCardResponse>(response, 'Failed to load report card');
}

export interface DialOpRosterQuery {
  state?: string[] | null;
  agent?: string[] | null;
  limit?: number;
  offset?: number;
  /** Rebuild from the warehouse instead of serving the cached roster (~1 min). */
  refresh?: boolean;
}

/**
 * The roster is anchored on the newest data in the warehouse rather than the
 * dashboard's selected period, so it deliberately takes no date arguments.
 */
export async function fetchDialOpLeadRoster({
  state,
  agent,
  limit = 100,
  offset = 0,
  refresh = false,
}: DialOpRosterQuery = {}): Promise<DialOpRosterResponse> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (refresh) params.set('refresh', 'true');
  for (const st of state ?? []) params.append('state', st);
  for (const a of agent ?? []) params.append('agent', a);
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/dialing-opportunity/lead-roster?${params.toString()}`),
    { headers: getAuthHeaders() },
  );
  return readJson<DialOpRosterResponse>(response, 'Failed to load lead roster');
}
